/**
 * Soft lifecycle cutoff — client mirror of the server-side gate in
 * towninsider-api/src/routes/chat.ts.
 *
 * Chat is open through the end of the day AFTER `stayEnd` in the
 * guest's local TZ. For `stayEnd = '2026-06-12'`, chat works through
 * end of 13 June and closes at 14 June 00:00 local.
 *
 * Why this lives on the client too:
 *   The server is authoritative for the actual gate (returns 403 with
 *   `code: 'stay_ended'` when cutoff has fired). Without a matching
 *   client check, a returning guest past the cutoff would see the
 *   chat input enabled, type a message, AND THEN get the closing
 *   line back — try-and-fail UX. With this check, MePage flips to
 *   memory mode on first paint and the user never sees an enabled
 *   input they can't use.
 *
 *   Server-side check still runs regardless — defence in depth, and
 *   the only authority for the gate. This module exists for UX.
 *
 * Why the date math duplicates the server:
 *   Both implementations are five lines testing the same condition.
 *   Cheap duplication, server stays authoritative on disagreement.
 *   Shared package would solve the duplication but isn't worth the
 *   plumbing at this scale.
 *
 * Returns true if the cutoff has fired (chat closed), false otherwise.
 * Malformed `stayEnd` input returns false — conservative fail-open on
 * the client; if the value is bad and the server agrees, the server
 * will gate and `MePage` flips memory mode via the onError path.
 */
export function isPastStayCutoff(
  stayEnd: string,
  todayLocal: string,
): boolean {
  const parts = stayEnd.split('-').map((s) => parseInt(s, 10));
  if (parts.length !== 3 || parts.some(Number.isNaN)) return false;
  const [y, m, d] = parts as [number, number, number];
  // Add 1 day via UTC anchor so calendar-day math stays correct across
  // month boundaries and DST changes. We work in calendar days, not
  // wall-clock hours, so UTC anchoring is the right primitive here.
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  const yy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  const lastOpenDay = `${yy}-${mm}-${dd}`;
  return todayLocal > lastOpenDay;
}
